import { Flex, Text, Grid, Circle, IconButton, Alert, AlertDescription, AlertIcon, AlertTitle } from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import React, { useEffect, useState } from "react";
import { useGetDevicesDataQuery } from "../../automations/api/automationsApi";
import { Card } from "./Card";
import { useDeviceStatusContext } from "../../../context/DeviceStatusContext";
import { findDeviceBrightness, findDeviceOutput } from "../../../utils/helpers";

const CARDS_PER_PAGE = 4;
const MAX_VISIBLE_DOTS = 4;

export function Controls() {
    const [currentPage, setCurrentPage] = useState(0);
    const [allDevices, setAllDevices] = useState<any[]>([])
    const [brightnessMap, setBrightnessMap] = useState<Record<number, number>>({})
    const { deviceStatusData } = useDeviceStatusContext()
    const totalPages = Math.ceil(allDevices?.length / CARDS_PER_PAGE);

    const handlePrev = () => {
        if (currentPage > 0) setCurrentPage(currentPage - 1);
    };

    const handleNext = () => {
        if (currentPage < totalPages - 1) setCurrentPage(currentPage + 1);
    };

    const getVisiblePageIndices = () => {
        if (totalPages <= MAX_VISIBLE_DOTS) {
            return Array.from({ length: totalPages }, (_, i) => i);
        }

        if (currentPage < 2) {
            return [0, 1, 2, 3];
        }

        if (currentPage >= totalPages - 2) {
            return [
                totalPages - 4,
                totalPages - 3,
                totalPages - 2,
                totalPages - 1,
            ];
        }

        return [
            currentPage - 1,
            currentPage,
            currentPage + 1,
            currentPage + 2,
        ];
    };

    const startIndex = currentPage * CARDS_PER_PAGE;
    const paginatedCards = allDevices?.slice(startIndex, startIndex + CARDS_PER_PAGE);

    const { data } = useGetDevicesDataQuery({})

    useEffect(() => {
        setCurrentPage(0);

        if (data?.results ) {
            const flattened = data?.results?.flatMap((device: any) =>
                device.model_channel_topics.map((param: any) => ({
                    ...param,
                    deviceId: device.id,
                    fullTitle: `${device.title} - ${param.title}`,
                    device,
                }))
            );

            setAllDevices(flattened);
        }
    }, [data]);

    return (
        <Flex
            height="max-content"
            direction="column"
            borderRadius="xl"
            p={4}
            bg="white"
            boxShadow="sm"
        >
            {/* Header */}
            <Flex mb={4}>
                <Text fontWeight={700} fontSize="18px" fontFamily="Inter" color="#484848">
                    Controls
                </Text>
            </Flex>

            {paginatedCards?.length > 0 ? (
                <>
                    {/* Cards */}
                    <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                        {paginatedCards.map((param) => (
                            <Card
                                key={`${param.deviceId}-${param.id}`}
                                device={param.device}
                                parameter={param.title}
                                brightness={brightnessMap[param.id] ?? findDeviceBrightness(param.device.id, param.title, deviceStatusData)}
                                toggle={findDeviceOutput(param.device.id, param.title, deviceStatusData)}
                                onBrightnessChange={(value) => setBrightnessMap((prev) => ({
                                    ...prev,
                                    [param.id]: value,
                                }))}
                                title={param.fullTitle}
                                modelChannelTopic={param?.model_chanel_topic?.id}
                            />
                        ))}
                    </Grid>

                    {/* Pagination Controls */}
                    <Flex justify="center" align="center" mt={4} gap={4}>
                        <IconButton
                            aria-label="Previous"
                            icon={<ChevronLeftIcon />}
                            onClick={handlePrev}
                            isDisabled={currentPage === 0}
                            variant="ghost"
                            size="sm"
                        />
                        <Flex gap={2}>
                            {getVisiblePageIndices().map((pageIndex) => (
                                <Circle
                                    key={pageIndex}
                                    size="8px"
                                    bg={pageIndex === currentPage ? "blue.500" : "gray.300"}
                                    cursor="pointer"
                                    onClick={() => setCurrentPage(pageIndex)}
                                />
                            ))}
                        </Flex>
                        <IconButton
                            aria-label="Next"
                            icon={<ChevronRightIcon />}
                            onClick={handleNext}
                            isDisabled={currentPage === totalPages - 1}
                            variant="ghost"
                            size="sm"
                        />
                    </Flex>
                </>
            ) : (
                <Alert
                    status="info"
                    bg="blue.50"
                    borderRadius="md"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    textAlign="center"
                    color="blue.800"
                >
                    <AlertIcon boxSize="40px" mr={0} color="blue.500" />
                    <AlertTitle mt={4} mb={1} fontSize="lg" fontWeight="bold">
                        No smart devices
                    </AlertTitle>
                    <AlertDescription maxWidth="sm" fontSize="sm" color="blue.700">
                        Currently there are no devices that support automation and smart control
                    </AlertDescription>
                </Alert>
            )}
        </Flex>
    );
}
